/**
 * HOW MANY PARTS A TEXT RUNS TO, and the most the phone system takes (ops #23). Its contract,
 * `faithfully-kept-phone/docs/api/facts.md`, refuses an instruction `422 too_long` at "more than
 * three segments, counted after the substitutions the carrier makes, so a curly quote does not
 * silently triple the cost". A composer counts the same way before it writes anything, so a text
 * the phone system would refuse is refused while the person sending it can still do something,
 * rather than parked afterwards with every owner told. It lives in the shared package since ops #32,
 * so the composer on the screen counts parts as they are typed with the function the server refuses
 * with, and `api/src/lib/sms.ts` re-exports it for the server's importers.
 *
 * THE RULE, as a text is carried. The carrier first makes the usual typographic characters plain:
 * curly quotes straight, the long dashes short, an ellipsis three full stops, a space that must not
 * break an ordinary one. Then, if every character is in the GSM 03.38 table, one part holds 160
 * and each part of several holds 153, a character from the table's extension counting two and never
 * split across a join. One character outside the table, an emoji or a letter it does not have, and
 * the whole text goes as sixteen-bit units instead, 70 to a single part and 67 to each of several,
 * a surrogate pair never split either.
 *
 * THE PHONE SYSTEM IS THE AUTHORITY, and this is ops's reading of the carrier's rule, not a copy of
 * the phone system's code, which ops never reads. Where the two disagree nothing is lost: a text
 * this lets through and the phone system still refuses is parked `too_long` with the owners told,
 * exactly as it was before this count existed.
 *
 * Every character below is written as its code point. A file of source is not where a lookalike or
 * an invisible character should be typed by hand, and gotcha 6 is what a stray one costs.
 */

/** The most parts the phone system takes in one text. */
export const MAX_TEXT_PARTS = 3;

const range = (from: number, to: number): number[] =>
  Array.from({ length: to - from + 1 }, (_, offset) => from + offset);

/**
 * The first two rows of the GSM 03.38 basic table, 0x00 to 0x1f, less the escape at 0x1b: @, the
 * pound, $, the yen, six accented letters, the line feed, O and o with a stroke, the carriage
 * return, A and a with a ring, the capital delta, the underscore, nine more Greek capitals, AE, ae,
 * the sharp s and E acute.
 */
const LOW_ROWS = [
  0x40, 0xa3, 0x24, 0xa5, 0xe8, 0xe9, 0xf9, 0xec, 0xf2, 0xc7, 0x0a, 0xd8, 0xf8, 0x0d, 0xc5, 0xe5,
  0x394, 0x5f, 0x3a6, 0x393, 0x39b, 0x3a9, 0x3a0, 0x3a8, 0x3a3, 0x398, 0x39e, 0xc6, 0xe6, 0xdf,
  0xc9,
];

/**
 * Where the rest of the table differs from ASCII: the currency sign in place of the dollar at 0x24;
 * the inverted exclamation mark, four accented capitals and the section sign around A to Z; the
 * inverted question mark and five accented small letters around a to z.
 */
const NOT_ASCII = [0xa4, 0xa1, 0xc4, 0xd6, 0xd1, 0xdc, 0xa7, 0xbf, 0xe4, 0xf6, 0xf1, 0xfc, 0xe0];

/** The GSM 03.38 basic table, one septet each. */
const BASIC: ReadonlySet<number> = new Set([
  ...LOW_ROWS,
  ...range(0x20, 0x23),
  ...range(0x25, 0x3f),
  ...range(0x41, 0x5a),
  ...range(0x61, 0x7a),
  ...NOT_ASCII,
]);

/**
 * The extension table, reached by the escape, so two septets each: the form feed, the caret, the
 * braces, the backslash, the brackets, the tilde, the bar and the euro sign.
 */
const EXTENSION: ReadonlySet<number> = new Set([
  0x0c, 0x5e, 0x7b, 0x7d, 0x5c, 0x5b, 0x7e, 0x5d, 0x7c, 0x20ac,
]);

const toEach = (codes: readonly number[], plain: string): [number, string][] =>
  codes.map((code) => [code, plain]);

/** The substitutions the carrier makes before it counts, the common typographic ones. */
const PLAIN: ReadonlyMap<number, string> = new Map([
  // Curly and prime single quotes, made straight.
  ...toEach([0x2018, 0x2019, 0x201a, 0x201b, 0x2032], "'"),
  // Curly, prime and angle double quotes, made straight.
  ...toEach([0x201c, 0x201d, 0x201e, 0x201f, 0x2033, 0xab, 0xbb], '"'),
  // The hyphens, the figure dash, the two long dashes, the bar and the minus sign, made short.
  ...toEach([0x2010, 0x2011, 0x2012, 0x2013, 0x2014, 0x2015, 0x2212], '-'),
  // The ellipsis, three full stops.
  ...toEach([0x2026], '...'),
  // The no-break, en, em, thin and narrow no-break spaces, an ordinary space.
  ...toEach([0xa0, 0x2002, 0x2003, 0x2009, 0x202f], ' '),
]);

const codeOf = (character: string): number => character.codePointAt(0) ?? 0;

/** How many parts these words go as, counted the way the carrier carries them. */
export function textParts(words: string): number {
  const letters = [...words].flatMap((character) => {
    const plain = PLAIN.get(codeOf(character));
    return plain === undefined ? [character] : [...plain];
  });
  const inTable = letters.every(
    (letter) => BASIC.has(codeOf(letter)) || EXTENSION.has(codeOf(letter)),
  );
  // What each character costs: septets in the table, sixteen-bit units outside it.
  const costs = letters.map((letter) =>
    inTable ? (EXTENSION.has(codeOf(letter)) ? 2 : 1) : letter.length,
  );
  const single = inTable ? 160 : 70;
  const each = inTable ? 153 : 67;
  if (costs.reduce((sum, cost) => sum + cost, 0) <= single) return 1;
  let parts = 1;
  let filled = 0;
  for (const cost of costs) {
    if (filled + cost > each) {
      parts += 1;
      filled = 0;
    }
    filled += cost;
  }
  return parts;
}
